'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import defaultConfig from '../config/defaultConfig.json';
import { validateConfig } from '../config/schema.js';

const ConfigContext = createContext();

/**
 * Provider para manejo de configuración dinámica del Sidenav
 * Permite cargar configuración desde JSON y validarla
 */
export function ConfigProvider({ 
  children, 
  config: initialConfig,
  onConfigChange,
  onValidationError 
}) {
  const [config, setConfig] = useState(initialConfig || defaultConfig);
  const [isValid, setIsValid] = useState(true);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Validar configuración cuando cambie
  useEffect(() => {
    const validation = validateConfig(config);
    setIsValid(validation.isValid);
    setValidationErrors(validation.errors);
    
    if (!validation.isValid && onValidationError) {
      onValidationError(validation.errors);
    }
  }, [config, onValidationError]);

  /**
   * Actualizar configuración con validación
   */
  const updateConfig = useCallback((newConfig, merge = true) => {
    try {
      const updatedConfig = merge ? { ...config, ...newConfig } : newConfig;
      setConfig(updatedConfig);
      
      if (onConfigChange) {
        onConfigChange(updatedConfig);
      }
      
      return { success: true, config: updatedConfig };
    } catch (error) {
      console.error('Error updating config:', error);
      return { success: false, error: error.message };
    }
  }, [config, onConfigChange]);

  /**
   * Cargar configuración desde URL o archivo
   */
  const loadConfig = useCallback(async (source) => {
    setIsLoading(true);
    try {
      let newConfig;
      
      if (typeof source === 'string') {
        // Cargar desde URL
        const response = await fetch(source);
        if (!response.ok) {
          throw new Error(`Failed to load config: ${response.statusText}`);
        }
        newConfig = await response.json();
      } else if (source instanceof File) {
        // Cargar desde archivo
        const text = await source.text();
        newConfig = JSON.parse(text);
      } else {
        // Configuración directa
        newConfig = source;
      }

      const validation = validateConfig(newConfig);
      if (!validation.isValid) {
        throw new Error(`Invalid configuration: ${validation.errors.join(', ')}`);
      }

      setConfig(newConfig);
      if (onConfigChange) {
        onConfigChange(newConfig);
      }
      
      return { success: true, config: newConfig };
    } catch (error) {
      console.error('Error loading config:', error);
      if (onValidationError) {
        onValidationError([error.message]);
      }
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [onConfigChange, onValidationError]);

  /**
   * Resetear a configuración por defecto
   */
  const resetConfig = useCallback(() => {
    setConfig(defaultConfig);
    if (onConfigChange) {
      onConfigChange(defaultConfig);
    }
  }, [onConfigChange]);

  /**
   * Obtener valor de configuración por path
   */
  const getConfigValue = useCallback((path, defaultValue = null) => {
    try {
      return path.split('.').reduce((obj, key) => obj?.[key], config) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  }, [config]);

  /**
   * Actualizar valor específico por path
   */
  const setConfigValue = useCallback((path, value) => {
    const keys = path.split('.');
    const newConfig = { ...config };
    let current = newConfig;
    
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current) || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }
    
    current[keys[keys.length - 1]] = value;
    return updateConfig(newConfig, false);
  }, [config, updateConfig]);

  /**
   * Exportar configuración actual
   */
  const exportConfig = useCallback(() => {
    return {
      config,
      timestamp: new Date().toISOString(),
      version: '1.0.0'
    };
  }, [config]);

  const contextValue = {
    // Estado
    config,
    isValid,
    validationErrors,
    isLoading,
    
    // Métodos
    updateConfig,
    loadConfig,
    resetConfig,
    getConfigValue,
    setConfigValue,
    exportConfig,
    
    // Configuraciones específicas
    navigation: config.navigation || [],
    logo: config.logo || {},
    theme: config.theme || {},
    settings: config.settings || {},
    localization: config.localization || {}
  };

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
}

/**
 * Hook para usar el contexto de configuración
 */
export function useConfig() {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

/**
 * Versión que no lanza: devuelve el contexto o `null` si no hay ConfigProvider.
 * La usan Sidenav y NavItem, que funcionan con o sin provider. Antes lo hacían
 * con `require()` dentro de un try/catch, que no existe en los builds ESM/UMD
 * (el provider se ignoraba siempre fuera de Storybook) y rompía las reglas de
 * los hooks.
 */
export function useOptionalConfig() {
  return useContext(ConfigContext) || null;
}

/**
 * Traducciones del ConfigProvider si existe, o `null`. Llama siempre a los
 * mismos hooks, haya provider o no.
 */
export function useOptionalLocalization() {
  const context = useContext(ConfigContext) || null;
  const [currentLanguage] = useState(
    context?.localization?.defaultLanguage || 'en'
  );
  if (!context) return null;
  const translate = (key, fallback = key) => {
    const translations = context.getConfigValue(
      `localization.languages.${currentLanguage}.translations`,
      {}
    );
    return translations[key] || fallback;
  };
  return { currentLanguage, translate, t: translate };
}

/**
 * Hook para obtener configuración específica de navegación
 */
export function useNavigation() {
  const { navigation, getConfigValue } = useConfig();
  
  return {
    items: navigation,
    defaultState: getConfigValue('settings.defaultState', 'open'),
    allowToggle: getConfigValue('settings.allowToggle', true),
    showTooltips: getConfigValue('settings.showTooltips', true)
  };
}

/**
 * Hook para obtener configuración del logo
 */
export function useLogo() {
  const { logo } = useConfig();

  const getVariant = (state) => {
    if (state === 'collapsed' && logo.variants?.collapsed) {
      return { ...logo, ...logo.variants.collapsed };
    }
    return logo;
  };

  const getCurrentLogo = (state) => getVariant(state);

  return {
    ...logo,
    getVariant,
    getCurrentLogo,
  };
}

/**
 * Hook para configuración de localización
 */
export function useLocalization() {
  const { localization, getConfigValue } = useConfig();
  const [currentLanguage, setCurrentLanguage] = useState(
    localization.defaultLanguage || 'en'
  );
  
  const translate = useCallback((key, fallback = key) => {
    const translations = getConfigValue(
      `localization.languages.${currentLanguage}.translations`,
      {}
    );
    return translations[key] || fallback;
  }, [currentLanguage, getConfigValue]);
  
  const changeLanguage = useCallback((language) => {
    const availableLanguages = Object.keys(localization.languages || {});
    if (availableLanguages.includes(language)) {
      setCurrentLanguage(language);
    }
  }, [localization.languages]);
  
  return {
    currentLanguage,
    availableLanguages: Object.keys(localization.languages || {}),
    translate,
    changeLanguage,
    t: translate // Alias corto
  };
}

export default ConfigProvider;